<template>
  <div class="fenxiao-order">
    <!-- 页面头部 -->
    <div class="page-header">
      <h2 class="page-title">推广订单</h2>
    </div>

    <!-- 状态导航栏 -->
    <div class="status-tabs">
      <div 
        v-for="tab in statusTabs" 
        :key="tab.id"
        class="tab-item"
        :class="{ active: currentStatus === tab.id }"
        @click="switchStatus(tab.id)"
      >
        {{ tab.name }}
      </div>
    </div>

    <!-- 订单列表容器 -->
    <div class="order-list-container">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner">
          <i class="el-icon-loading"></i>
          <span>加载中...</span>
        </div>
      </div>

      <!-- 订单列表 -->
      <div v-else-if="orderList.length > 0" class="order-list">
        <div 
          v-for="order in orderList" 
          :key="order.fenxiao_order_id"
          class="order-item"
          @click="goToOrderDetail(order)"
        >
          <!-- 订单头部信息 -->
          <div class="order-header">
            <div class="order-info">
              <span class="order-no">订单号：{{ order.order_no }}</span>
              <span class="order-time">{{ formatTime(order.create_time) }}</span>
            </div>
            <div class="order-status">
              <span :class="getOrderStatusClass(order)">
                {{ getOrderStatusText(order) }}
              </span>
            </div>
          </div>

          <!-- 商品信息 -->
          <div class="goods-info">
            <div class="goods-image">
              <img :src="$util.img(order.sku_image)" :alt="order.sku_name" />
            </div>
            <div class="goods-details">
              <div class="goods-name">{{ order.sku_name }}</div>
              <div class="goods-spec" v-if="order.sku_spec_format">
                {{ order.sku_spec_format }}
              </div>
              <div class="goods-price">
                <span class="price">¥{{ order.price }}</span>
                <span class="quantity">x{{ order.num }}</span>
              </div>
            </div>
          </div>

          <!-- 佣金信息 -->
          <div class="commission-info">
            <div class="commission-item">
              <span class="label">预计佣金：</span>
              <span class="amount">¥{{ order.commission || '0.00' }}</span>
            </div>
            <div class="commission-item" v-if="order.commission_rate">
              <span class="label">佣金比例：</span>
              <span class="rate">{{ order.commission_rate }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="empty-state">
        <div class="empty-icon">
          <img :src="$util.img('upload/uniapp/fenxiao/order/empty.png')" alt="暂无订单" />
        </div>
        <div class="empty-text">暂无相关订单</div>
      </div>

      <!-- 分页组件 -->
      <div v-if="orderList.length > 0" class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import { getFenxiaoOrderList } from '@/api/fenxiao/order'

export default {
  name: 'FenxiaoOrder',
  data() {
    return {
      // 状态导航栏配置
      statusTabs: [
        { id: '', name: '全部' },
        { id: '1', name: '待结算' },
        { id: '2', name: '已结算' },
        { id: '3', name: '已退款' }
      ],
      // 当前选中状态
      currentStatus: '',
      // 订单列表
      orderList: [],
      // 加载状态
      loading: false,
      // 分页信息
      currentPage: 1,
      pageSize: 10,
      total: 0
    }
  },
  mounted() {
    this.getOrderList()
  },
  methods: {
    // 切换状态
    switchStatus(status) {
      if (this.currentStatus === status) return
      
      this.currentStatus = status
      this.currentPage = 1
      this.getOrderList()
    },

    // 获取订单列表
    async getOrderList() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          limit: this.pageSize
        }
        
        // 添加状态筛选
        if (this.currentStatus !== '') {
          params.is_settlement = this.currentStatus
        }

        const response = await getFenxiaoOrderList(params)
        
        if (response.code === 0 && response.data) {
          const newList = response.data.list || []
          
          // 分页模式：直接替换列表数据
          this.orderList = newList
          this.total = response.data.count || 0
        } else {
          this.$message.error(response.message || '获取订单列表失败')
        }
      } catch (error) {
        console.error('获取订单列表失败:', error)
        this.$message.error('获取订单列表失败')
      } finally {
        this.loading = false
      }
    },

    // 分页 - 每页数量改变
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.getOrderList()
    },

    // 分页 - 当前页改变
    handleCurrentChange(val) {
      this.currentPage = val
      this.getOrderList()
    },

    // 跳转到订单详情
    goToOrderDetail(order) {
      this.$router.push({
        path: '/member/fenxiao_order_detail',
        query: { id: order.fenxiao_order_id }
      })
    },

    // 获取订单状态样式类
    getOrderStatusClass(order) {
      if (order.is_refund == 1) {
        return 'status-refunded'
      } else if (order.is_settlement == 1) {
        return 'status-settled'
      } else {
        return 'status-pending'
      }
    },

    // 获取订单状态文本
    getOrderStatusText(order) {
      if (order.is_refund == 1) {
        return '已退款'
      } else if (order.is_settlement == 1) {
        return '已结算'
      } else {
        return '待结算'
      }
    },

    // 获取状态样式类
    getStatusClass(status) {
      const statusMap = {
        '1': 'status-pending',
        '2': 'status-settled',
        '3': 'status-refunded'
      }
      return statusMap[status] || 'status-default'
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        '1': '待结算',
        '2': '已结算',
        '3': '已退款'
      }
      return statusMap[status] || '未知状态'
    },

    // 格式化时间
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.fenxiao-order {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;

  .page-header {
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .page-title {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #333;
    }
  }

  .status-tabs {
    display: flex;
    background: #fff;
    border-radius: 8px;
    padding: 4px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .tab-item {
      flex: 1;
      text-align: center;
      padding: 12px 16px;
      cursor: pointer;
      border-radius: 6px;
      transition: all 0.3s ease;
      font-weight: 500;
      color: #666;

      &:hover {
        color: #ff547b;
        background: rgba(255, 84, 123, 0.1);
      }

      &.active {
        background: #ff547b;
        color: #fff;
        box-shadow: 0 2px 8px rgba(255, 84, 123, 0.3);
      }
    }
  }

  .order-list-container {
    .loading-container {
      background: #fff;
      border-radius: 8px;
      padding: 60px 20px;
      text-align: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .order-list {
      .order-item {
        background: #fff;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 16px;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        }

        .order-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f0f0f0;

          .order-info {
            .order-no {
              font-weight: 600;
              color: #333;
              margin-right: 16px;
            }

            .order-time {
              color: #999;
              font-size: 14px;
            }
          }

          .order-status {
            .status-pending {
              color: #ff9500;
              background: rgba(255, 149, 0, 0.1);
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
            }

            .status-settled {
              color: #52c41a;
              background: rgba(82, 196, 26, 0.1);
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
            }

            .status-refunded {
              color: #f5222d;
              background: rgba(245, 34, 45, 0.1);
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
            }

            .status-default {
              color: #666;
              background: rgba(102, 102, 102, 0.1);
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
            }
          }
        }

        .goods-info {
          display: flex;
          margin-bottom: 16px;

          .goods-image {
            width: 80px;
            height: 80px;
            border-radius: 8px;
            overflow: hidden;
            margin-right: 16px;
            flex-shrink: 0;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
          }

          .goods-details {
            flex: 1;

            .goods-name {
              font-weight: 500;
              color: #333;
              margin-bottom: 8px;
              line-height: 1.4;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }

            .goods-spec {
              color: #999;
              font-size: 14px;
              margin-bottom: 8px;
            }

            .goods-price {
              display: flex;
              align-items: center;
              justify-content: space-between;

              .price {
                color: #ff547b;
                font-weight: 600;
                font-size: 16px;
              }

              .quantity {
                color: #666;
                font-size: 14px;
              }
            }
          }
        }

        .commission-info {
          display: flex;
          justify-content: space-between;
          padding-top: 12px;
          border-top: 1px solid #f0f0f0;

          .commission-item {
            .label {
              color: #666;
              font-size: 14px;
            }

            .amount {
              color: #ff547b;
              font-weight: 600;
              margin-left: 4px;
            }

            .rate {
              color: #52c41a;
              font-weight: 500;
              margin-left: 4px;
            }
          }
        }
      }
    }

    .empty-state {
      background: #fff;
      border-radius: 8px;
      padding: 60px 20px;
      text-align: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

      .empty-icon {
        margin-bottom: 16px;

        img {
          width: 120px;
          height: 120px;
          opacity: 0.6;
        }
      }

      .empty-text {
        color: #999;
        font-size: 16px;
      }
    }

    /* 分页样式 */
    .pagination-container {
      display: flex;
      justify-content: center;
      margin-top: 30px;
      padding: 20px 0;
    }

    .pagination-container .el-pagination {
      text-align: center;
    }
  }
}
</style>